/* jshint esversion: 6 */
import Q from 'q';
import { map as Qmap } from 'q-map-throttle-ll';
import UploadAnalytics from '../upload-analytics';
import UploadTimings from '../upload-timings';
import { sessionTracker } from '../../_prism-builder/utils/session-tracker';

import showLoginOverlay from '../utils/show-login-overlay';
import { compressOrFallback } from '../compression/browser-image-compressor';
import { createConcurrencyLimiter } from '../compression/concurrency-limiter';
import {
  CompressionMode,
  resolveCompressionMode,
  resolveCompressionQuality,
  resolveCompressionConcurrency,
} from '../compression/compression-flags';

Q.map = Qmap;

// 4xx client errors (other than timeout/rate-limit) are terminal: the request
// will never succeed on retry, so do not re-upload. 408 (timeout) and 429 (rate-limit) stay retryable.
export const isNonRetryableUploadStatus = (status) =>
  status >= 400 && status < 500 && status !== 408 && status !== 429;

const GenericPaginatedUploadMixin = {
  defaultWindowSize: 6,
  defaultRetryAttempts: 5,

  getInitialState() {
    return {
      uploadPaused: false,
    };
  },
  UNSAFE_componentWillMount() {
    this.uploadPaused = null;
  },

  performUpload(
    uploadUrl = this.params && this.params.uploadUrl,
    additionalData = this.params && this.params.additionalData,
    onUploaded = this.params && this.params.onUploaded,
    onUploadError = this.params && this.params.onUploadError,
  ) {
    this.params || (this.params = {});
    this.params.uploadUrl = uploadUrl;
    this.params.additionalData = additionalData;
    this.params.onUploaded = onUploaded;
    this.params.onUploadError = onUploadError;

    const uploadTransactionId = generateId('fup');

    const component = this;
    const windowSize = this.windowSize || this.defaultWindowSize;
    const retryAttempts = this.retryAttempts || this.defaultRetryAttempts;
    const files = component.filesSelected().filter((file) => !file.uploaded);
    const fileUploadSource =
      additionalData?.uploadSource || component.props.service;

    // Compression mode is resolved once for the whole batch and decides how
    // each file is prepared before upload:
    //   BROWSER - compress in-browser here (throttled), then upload the result;
    //             on failure or no useful reduction, upload the original.
    //   NONE    - upload the raw file as-is.
    // Two independent throttles: runCompression caps concurrent compressions
    // (memory), Q.map(windowSize) caps concurrent uploads (network).
    const compressionMode = resolveCompressionMode();
    const compressionQuality = resolveCompressionQuality();
    const runCompression = createConcurrencyLimiter(
      resolveCompressionConcurrency(),
    );

    // Uniform per-file initialisation
    files.forEach((f) => {
      f.attempts = 0;
      f.uploadTimings = new UploadTimings();
      f.uploadTimings.started();
      f.uploadAnalytics = new UploadAnalytics({
        fileName: f.name,
        uploadTransactionId,
        uploadTimings: f.uploadTimings,
        fileUploadSource,
        fileType: f.type || null,
        batchSize: files.length,
      });
      f.uploadAnalytics.setFileSizes(f.size);
    });

    // Drive the upload window: at most windowSize sendFile calls run at once,
    // each settling its deferred when its upload finishes (or is abandoned),
    // which frees the slot for the next file. A paused/abandoned file rejects
    // its deferred with no reason, so Q.all rejects; that expected case is
    // silent, but anything with an actual reason is a real error worth surfacing.
    Q.map(files, sendFile, windowSize).catch((err) => {
      if (err) {
        console.debug('Upload window ended with an unexpected rejection:', err); // eslint-disable-line no-console
      }
    });

    function sendFile(rawFile) {
      const deferred = Q.defer();

      if (component.paused()) {
        deferred.reject();
        return deferred.promise;
      }

      if (
        compressionMode === CompressionMode.BROWSER &&
        !rawFile.browserCompressed
      ) {
        // BROWSER mode: compress first (bounded by runCompression), then upload
        // the result. Skipped when rawFile is already a compressor output from
        // an earlier pass (e.g. a 401 pause + restart re-runs performUpload over
        // files that compressed but had not finished uploading) so we do not
        // re-encode an already compressed JPEG into a second generation of quality loss.
        runCompression(() => {
          rawFile.uploadTimings.compressionStarted();
          return compressOrFallback(rawFile, { quality: compressionQuality });
        })
          .then(({ file: uploadFile, compressed, reason, error }) => {
            rawFile.uploadTimings.compressionFinished();
            // A new compressed File was produced: carry the per-file metadata
            // onto it and swap it into component.files so retries re-send it.
            if (uploadFile !== rawFile) {
              uploadFile.attempts = rawFile.attempts;
              uploadFile.service = rawFile.service;
              uploadFile.uploadTimings = rawFile.uploadTimings;
              uploadFile.uploadAnalytics = rawFile.uploadAnalytics;
              const idx = component.files.indexOf(rawFile);
              if (idx !== -1) component.files[idx] = uploadFile;
            }
            uploadFile.uploadAnalytics.upload_source = compressed
              ? 'local-compression'
              : 'raw';
            uploadFile.uploadAnalytics.compressionResult = reason;
            if (compressed) {
              // Mark it so a later performUpload pass (e.g. after a 401
              // restart) uploads it as-is instead of re-compressing.
              uploadFile.browserCompressed = true;
              uploadFile.uploadTimings.compressed();
              uploadFile.uploadAnalytics.setFileSizes(
                rawFile.size,
                uploadFile.size,
              );
              const savedPct = Math.round(
                (1 - uploadFile.size / rawFile.size) * 100,
              );
              // eslint-disable-next-line no-console
              console.debug(
                `name:${uploadFile.name} `,
                `Browser-compressed ${rawFile.size} -> ${uploadFile.size} bytes (${savedPct}% smaller)`,
              );
            } else if (reason === 'incomplete') {
              // eslint-disable-next-line no-console
              console.debug(
                `name:${rawFile.name} `,
                'File looks truncated; uploading original so the server can reject it.',
              );
            } else if (reason === 'error') {
              // eslint-disable-next-line no-console
              console.debug(
                `name:${rawFile.name} `,
                'Browser compression failed; uploading original. Cause:',
                error,
              );
            } else {
              // eslint-disable-next-line no-console
              console.debug(
                `name:${rawFile.name} `,
                'Browser compression skipped (no meaningful size reduction); uploading original.',
              );
            }
            beginSend(uploadFile);
          })
          .catch((err) => {
            // compressOrFallback never rejects, so reaching here means the
            // .then body threw unexpectedly. Stay fail-safe: upload the
            // original rather than dropping the file.
            // eslint-disable-next-line no-console
            console.debug(
              `name:${rawFile.name} `,
              'Unexpected error preparing the upload; uploading original.',
              err,
            );
            rawFile.uploadAnalytics.upload_source = 'raw';
            rawFile.uploadAnalytics.compressionResult = 'error';
            try {
              beginSend(rawFile);
            } catch (e) {
              // Even the original could not be sent; surface it and settle the
              // slot so the window is not wedged.
              // eslint-disable-next-line no-console
              console.debug(
                `name:${rawFile.name} `,
                'Fallback upload failed.',
                e,
              );
              onUploadError();
              deferred.resolve();
            }
          });
      } else {
        // NONE mode, or a BROWSER file already compressed on an earlier pass:
        // no compression step here, upload the file as-is. Re-tag the
        // already-compressed case (its analytics is freshly reset each pass);
        // NONE gets tagged in _send.
        if (rawFile.browserCompressed) {
          rawFile.uploadAnalytics.upload_source = 'local-compression';
          rawFile.uploadAnalytics.compressionResult = 'compressed';
        }

        beginSend(rawFile);
      }

      return deferred.promise;

      // Shared upload delegator for every mode: build the form data and run the
      // POST with retry/pause handling.
      function beginSend(file) {
        _send(file, buildFormData(file, additionalData));
      }

      function _send(file, data) {
        if (component.paused()) {
          deferred.reject();
          return;
        }

        // BROWSER mode already tagged the source (local-compression/raw) when
        // compression resolved; only tag here for NONE mode.
        if (compressionMode !== CompressionMode.BROWSER) {
          file.uploadAnalytics.upload_source = 'raw';
        }
        // eslint-disable-next-line no-console
        console.debug(
          file.name,
          `Uploading file via File (${file.uploadAnalytics.upload_source})`,
        );
        if (
          typeof component.makesTooManyRequests === 'function' &&
          component.makesTooManyRequests(file.service)
        ) {
          onUploadError(
            `${
              file.service.charAt(0).toUpperCase() + file.service.slice(1)
            } is currently unavailable, please use other service to upload your photos`,
          );
          deferred.resolve();
          return;
        }

        file.uploadTimings.uploadStarted();
        $.ajax(uploadUrl, {
          data,
          processData: false,
          contentType: false,
          type: 'POST',
        })
          .done((data, _textStatus, _jqXHR) => {
            // file is exactly what was sent, browser-compressed or original.
            // No-op outside a prism builder session.
            sessionTracker.trackUploadedImage({
              sentBytes: file.size,
              pickedBytes: file.uploadAnalytics?.originalSize,
            });
            onDone(data, file);
          })
          .fail((jqXHR, _textStatus, _errorThrown) => {
            onFail(jqXHR, file);
          });
      }

      // Shared success path for every mode: mark uploaded, hand the image data
      // to the caller, settle the slot, and emit analytics.
      function onDone(data, file) {
        if (typeof window.newrelic !== 'undefined') {
          window.newrelic.setCustomAttribute('uploadSourceData', {
            data,
            file,
          });
        }
        const imageData =
          (data?.result || {}).image || (data?.result || {}).images;

        file.uploaded = true;
        file.uploadTimings.uploaded();

        if (!file.uploadTimings.compression) {
          // eslint-disable-next-line no-console
          console.debug(
            `name:${file.name} `,
            'Uploaded without compression.',
            file.uploadTimings,
          );
        }

        onUploaded(imageData, component.props.service);
        deferred.resolve();

        file.uploadTimings.completed((uploadTimings) => {
          if (uploadTimings.compression > uploadTimings.upload) {
            console.error(
              '[Upload Stats] Compression time greater than e2e time:',
              uploadTimings,
              file,
            );
          }
        });
        file.uploadAnalytics.sendObservability({ attempts: file.attempts });

        console.debug(`name:${file.name} `, 'Done!', file.uploadTimings); // eslint-disable-line no-console
      }

      // Shared failure path for the $.ajax upload: 401 -> pause + re-auth then
      // restart; terminal 4xx or error-limit -> give up; otherwise retry up to
      // retryAttempts, re-sending the same (already-compressed) file.
      function onFail(jqXHR, file) {
        // eslint-disable-next-line no-console
        console.debug(
          `name:${file.name} `,
          'Upload failed.',
          file.uploadTimings,
        );
        const notAuthorized =
          jqXHR && (jqXHR.status === 401 || jqXHR.status_code === 401);

        if (notAuthorized) {
          component.pause();
          deferred.resolve();
          showLoginOverlay().done(() => {
            component.restart();
          });
          return;
        }

        const failureStatus =
          (jqXHR && (jqXHR.status || jqXHR.status_code)) || 0;
        if (isNonRetryableUploadStatus(failureStatus)) {
          onUploadError();
          deferred.resolve();
          return;
        }

        if (
          typeof component.reachedErrorLimit === 'function' &&
          component.reachedErrorLimit(file.service)
        ) {
          onUploadError();
          deferred.resolve();
          return;
        }

        if (typeof component.saveErrors === 'function') {
          component.saveErrors(file.service, jqXHR.status || jqXHR.status_code);
        }

        if (file.attempts++ < retryAttempts) {
          _send(file, buildFormData(file, additionalData));
          return;
        }

        onUploadError();
        deferred.resolve();
      }
    }

    function buildFormData(file, additionalData) {
      const formData = new FormData();
      const { uploadSource: uploadSourceOverride, ...otherParams } =
        additionalData;
      const uploadSource = uploadSourceOverride || component.props.service;

      formData.set('source', uploadSource);
      Object.entries(otherParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      component._buildFormData(formData, file);

      return formData;
    }

    function generateId(prefix) {
      function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      }
      return `${prefix}${s4()}${s4()}${s4()}${s4()}`;
    }
  },

  pause() {
    this.setState({
      uploadPaused: true,
    });
  },
  restart() {
    this.setState(
      {
        uploadPaused: false,
      },
      function () {
        this.performUpload();
      },
    );
  },
  paused() {
    return this.state.uploadPaused;
  },
};

export default GenericPaginatedUploadMixin;
